import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  onboardingNexHealthQueryKey,
  onboardingNexHealthQueryOptions,
  onboardingStatusQueryKey,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './NexHealthPage.module.css'

export function NexHealthLocationPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedLocationPage
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedLocationPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))
  if (statusQuery.isPending || statusQuery.isFetching) {
    return <Spinner label="Checking your setup" />
  }
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <StatePanel
        kind="error"
        title="Location setup unavailable"
        description="Retry the setup check before selecting a location."
      />
    )
  }
  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'nexhealth-location') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return (
    <LocationSelector client={client} user={user} onSignOut={auth.logout} />
  )
}

function LocationSelector({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const query = useQuery(onboardingNexHealthQueryOptions(client, user))
  const [selected, setSelected] = useState<number | null>(null)
  const [working, setWorking] = useState(false)
  const [stale, setStale] = useState(false)

  const refreshStatus = async () => {
    await queryClient.invalidateQueries({
      queryKey: onboardingStatusQueryKey(user.uid),
    })
  }

  const refreshLocations = async () => {
    if (working || !query.data) return
    setWorking(true)
    setStale(false)
    try {
      const response = await client.discoverNexHealth(user, query.data.revision)
      queryClient.setQueryData(onboardingNexHealthQueryKey(user.uid), response)
      setSelected(
        selected !== null &&
          response.locations.some((item) => item.id === selected)
          ? selected
          : null
      )
      await refreshStatus()
    } catch (error) {
      if (
        error instanceof OnboardingApiError &&
        ['revision-conflict', 'stale-discovery'].includes(error.code)
      ) {
        setStale(true)
        setSelected(null)
      }
      await Promise.all([query.refetch(), refreshStatus()])
    } finally {
      setWorking(false)
    }
  }

  const continueSetup = async () => {
    if (working || selected === null || !query.data) return
    setWorking(true)
    setStale(false)
    try {
      const response = await client.selectNexHealthLocation(
        user,
        query.data.revision,
        query.data.discoveryRevision,
        selected
      )
      queryClient.setQueryData(onboardingNexHealthQueryKey(user.uid), response)
      await refreshStatus()
    } catch (error) {
      if (
        error instanceof OnboardingApiError &&
        ['revision-conflict', 'stale-discovery'].includes(error.code)
      ) {
        setStale(true)
        setSelected(null)
      }
      await Promise.all([query.refetch(), refreshStatus()])
    } finally {
      setWorking(false)
    }
  }

  if (query.isPending) return <Spinner label="Loading NexHealth locations" />
  if (query.isError) {
    return (
      <StatePanel
        kind="error"
        title="Locations could not be loaded"
        description="Retry the page to load the latest NexHealth locations."
      />
    )
  }

  return (
    <Card className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>NexHealth connection</span>
        <h1>Choose your clinic location</h1>
        <p>Select the NexHealth location Flio should configure next.</p>
      </div>
      {stale && (
        <StatePanel
          kind="error"
          title="Locations changed"
          description="The latest locations were loaded. Review them and select again."
        />
      )}
      <fieldset className={styles.locations} disabled={working}>
        <legend className={styles.srOnly}>Available locations</legend>
        {query.data.locations.map((item) => (
          <label className={styles.location} key={item.id}>
            <input
              type="radio"
              name="nexhealth-location"
              checked={selected === item.id}
              onChange={() => setSelected(item.id)}
            />
            <span>{item.name}</span>
          </label>
        ))}
      </fieldset>
      <div className={styles.actions}>
        <Button
          type="button"
          disabled={working || selected === null}
          onClick={() => void continueSetup()}
        >
          {working ? 'Saving…' : 'Continue'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void refreshLocations()}
        >
          Refresh locations
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}
